'use client';

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

export interface EyeOffset {
  x: number;
  y: number;
}

export interface EyeTrackingOptions {
  /** Maximum outward horizontal pupil movement in SVG units (default: 10) */
  maxOffsetOutward?: number;
  /** Maximum upward pupil movement in SVG units (default: 4) */
  maxOffsetUp?: number;
  /** Maximum downward pupil movement in SVG units (default: 14) */
  maxOffsetDown?: number;
  /** Sensitivity divisor - higher = less movement (default: 80) */
  sensitivity?: number;
  /** Container dimensions for coordinate mapping */
  containerWidth?: number;
  containerHeight?: number;
  /** Gaze target X (midpoint between the pupils) in SVG units (default: 98.75, the legacy head) */
  faceCenterX?: number;
  /** Gaze target Y (eye line) in SVG units (default: 47, the legacy head) */
  faceCenterY?: number;
  /** Whether tracking is enabled (default: true) */
  enabled?: boolean;
  /** Enable eye wander when cursor is idle (default: true) */
  enableWander?: boolean;
  /** Time in ms before eyes start wandering when idle (default: 2000) */
  idleTimeout?: number;
  /** Time in ms before eyes reset to center after mouse leaves (default: 600) */
  resetDelay?: number;
  /** Interval range in ms between wander movements [min, max] (default: [1500, 3000]) */
  wanderInterval?: [number, number];
}

export interface EyeTrackingResult {
  /** Ref to attach to the container element */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Shared eye offset (both eyes move together) */
  eyeOffset: EyeOffset;
  /** Whether eyes are in idle wander mode */
  isIdle: boolean;
}

// Default face center point (midpoint between eyes) for gaze calculation — legacy head geometry.
// Overridable via the faceCenterX/faceCenterY options for heads with a different viewBox.
const DEFAULT_FACE_CENTER_X = 98.75; // (33.5 + 164) / 2
const DEFAULT_FACE_CENTER_Y = 47;

// Wander distance factors - controls how far pupils move during idle wander
const MIN_WANDER_DISTANCE_FACTOR = 0.3; // Minimum wander distance (30% of max)
const WANDER_DISTANCE_RANGE = 0.4; // Additional random range (up to 70% total)

/**
 * Hook for tracking cursor position and calculating eye pupil offsets.
 * Both eyes move together based on a single gaze point (center of face).
 *
 * Uses requestAnimationFrame to throttle calculations to the display
 * refresh rate, avoiding unnecessary state updates on every mousemove.
 */
export function useEyeTracking(options: EyeTrackingOptions = {}): EyeTrackingResult {
  const {
    maxOffsetOutward = 10,
    maxOffsetUp = 4,
    maxOffsetDown = 14,
    sensitivity = 80,
    containerWidth = 197,
    containerHeight = 103.567,
    faceCenterX = DEFAULT_FACE_CENTER_X,
    faceCenterY = DEFAULT_FACE_CENTER_Y,
    enabled = true,
    enableWander = true,
    idleTimeout = 2000,
    resetDelay = 600,
    wanderInterval = [1500, 3000],
  } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const [eyeOffset, setEyeOffset] = useState<EyeOffset>({ x: 0, y: 0 });
  const [isMouseInWindow, setIsMouseInWindow] = useState(true);
  const [isIdle, setIsIdle] = useState(false);
  const [wanderTarget, setWanderTarget] = useState<EyeOffset>({ x: 0, y: 0 });

  const mousePosRef = useRef({ x: 0, y: 0 });
  const rafIdRef = useRef<number>(0);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wanderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync options into ref so rAF callback reads latest values without re-attaching listeners
  const optionsRef = useRef({
    maxOffsetOutward,
    maxOffsetUp,
    maxOffsetDown,
    sensitivity,
    containerWidth,
    containerHeight,
    faceCenterX,
    faceCenterY,
    wanderIntervalMin: wanderInterval[0],
    wanderIntervalMax: wanderInterval[1],
  });
  optionsRef.current = {
    maxOffsetOutward,
    maxOffsetUp,
    maxOffsetDown,
    sensitivity,
    containerWidth,
    containerHeight,
    faceCenterX,
    faceCenterY,
    wanderIntervalMin: wanderInterval[0],
    wanderIntervalMax: wanderInterval[1],
  };

  const updateEyeOffset = useCallback(() => {
    if (!containerRef.current) return;

    const {
      maxOffsetOutward: maxOut,
      maxOffsetUp: maxUp,
      maxOffsetDown: maxDown,
      sensitivity: sens,
      containerWidth: cw,
      containerHeight: ch,
      faceCenterX: fcx,
      faceCenterY: fcy,
    } = optionsRef.current;

    const rect = containerRef.current.getBoundingClientRect();
    const faceX = rect.left + (fcx / cw) * rect.width;
    const faceY = rect.top + (fcy / ch) * rect.height;

    const dx = mousePosRef.current.x - faceX;
    const dy = mousePosRef.current.y - faceY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance === 0) {
      setEyeOffset({ x: 0, y: 0 });
      return;
    }

    const angle = Math.atan2(dy, dx);
    const normalizedDistance = Math.min(distance / sens, 1);
    const maxX = maxOut;
    const maxY = dy < 0 ? maxUp : maxDown;

    setEyeOffset({
      x: Math.cos(angle) * normalizedDistance * maxX,
      y: Math.sin(angle) * normalizedDistance * maxY,
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleMouseMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
      setIsMouseInWindow(true);
      setIsIdle(false);

      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

      if (enableWander) {
        idleTimerRef.current = setTimeout(() => setIsIdle(true), idleTimeout);
      }

      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(updateEyeOffset);
    };

    const handleMouseLeave = () => {
      setIsMouseInWindow(false);
      setIsIdle(false);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);

      resetTimerRef.current = setTimeout(() => {
        setEyeOffset({ x: 0, y: 0 });
        resetTimerRef.current = null;
      }, resetDelay);
    };

    const handleMouseEnter = () => {
      setIsMouseInWindow(true);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    if (enableWander) {
      idleTimerRef.current = setTimeout(() => setIsIdle(true), idleTimeout);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, [enabled, enableWander, idleTimeout, resetDelay, updateEyeOffset]);

  // Idle wander — random saccade-like eye movements when cursor is stationary
  useEffect(() => {
    if (!enableWander || !isIdle || !isMouseInWindow) {
      if (wanderTimerRef.current) {
        clearTimeout(wanderTimerRef.current);
        wanderTimerRef.current = null;
      }
      return;
    }

    let isCancelled = false;

    const generateWanderTarget = (): EyeOffset => {
      const { maxOffsetOutward: maxOut, maxOffsetUp: maxUp, maxOffsetDown: maxDown } = optionsRef.current;
      const angle = Math.random() * 2 * Math.PI;
      const distanceFactor = MIN_WANDER_DISTANCE_FACTOR + Math.random() * WANDER_DISTANCE_RANGE;

      return {
        x: Math.cos(angle) * maxOut * distanceFactor,
        y: Math.sin(angle) * (Math.sin(angle) < 0 ? maxUp : maxDown) * distanceFactor,
      };
    };

    const getRandomWanderInterval = () => {
      const { wanderIntervalMin, wanderIntervalMax } = optionsRef.current;
      return Math.random() * (wanderIntervalMax - wanderIntervalMin) + wanderIntervalMin;
    };

    const scheduleWander = () => {
      if (isCancelled) return;

      wanderTimerRef.current = setTimeout(() => {
        if (isCancelled) return;
        setWanderTarget(generateWanderTarget());
        if (!isCancelled) {
          scheduleWander();
        }
      }, getRandomWanderInterval());
    };

    setWanderTarget(generateWanderTarget());
    scheduleWander();

    return () => {
      isCancelled = true;
      if (wanderTimerRef.current) {
        clearTimeout(wanderTimerRef.current);
        wanderTimerRef.current = null;
      }
    };
  }, [enableWander, isIdle, isMouseInWindow]);

  useEffect(() => {
    if (isIdle && enableWander && isMouseInWindow) {
      setEyeOffset(wanderTarget);
    }
  }, [isIdle, enableWander, isMouseInWindow, wanderTarget]);

  useEffect(() => {
    if (!enabled) {
      setEyeOffset({ x: 0, y: 0 });
    }
  }, [enabled]);

  return {
    containerRef,
    eyeOffset,
    isIdle,
  };
}
